import { getResData, httpClient } from "utils";
import {
  SiteDTO,
  SitesPaginatedData,
  IntensityDTO,
  CreateSiteDTO,
  SiteByIdDTO,
  UpdateSite,
  UpdateIntensity,
  CreateEstimateDTO,
  UpdateEstimate,
  EstimateDTO,
  DistributionDTO,
  RenewableEnergyPurchasesDTO,
  SitesPaginatedQuery,
  SitesPaginatedRequestBody,
} from "./types";
import {
  INTENSITY_API_PATHS,
  INTENSITYSITE_API_PATHS,
  SITES_API_PATHS,
  RENEWABLE_ENERGY_PURCHASES_API_PATHS,
} from "./consts";

export const getSites = (): Promise<SiteDTO[]> =>
  httpClient.get<SiteDTO[]>(SITES_API_PATHS.SITES).then(getResData);

export const getSitesWithPagination = (
  query: SitesPaginatedQuery,
  requestBody?: SitesPaginatedRequestBody,
): Promise<SitesPaginatedData> =>
  httpClient
    .post<SitesPaginatedData>(
      SITES_API_PATHS.SITES_PAGINATED(query),
      requestBody,
    )
    .then(getResData);

export const getIntensity = (): Promise<IntensityDTO[]> =>
  httpClient
    .get<IntensityDTO[]>(INTENSITY_API_PATHS.INTENSITY)
    .then(getResData);

export const getIntensitysite = (): Promise<IntensityDTO[]> =>
  httpClient
    .get<IntensityDTO[]>(INTENSITYSITE_API_PATHS.INTENSITYSITE)
    .then(getResData);

export const createSite = (body: CreateSiteDTO) =>
  httpClient.post(SITES_API_PATHS.SITES, body);

export const getSiteById = (id: number): Promise<SiteByIdDTO> =>
  httpClient.get<SiteByIdDTO>(SITES_API_PATHS.SITE(id)).then(getResData);

export const updateSite = (data: UpdateSite) =>
  httpClient.put(SITES_API_PATHS.SITES, data);

export const addDataProvider = (siteId: number, userId: number): Promise<any> =>
  httpClient.put(SITES_API_PATHS.SITE_USER(siteId, userId));

export const deleteDataProvider = (
  siteId: number,
  userId: number,
): Promise<any> => httpClient.delete(SITES_API_PATHS.SITE_USER(siteId, userId));

export const updateIntensity = (data: UpdateIntensity) =>
  httpClient.put(INTENSITY_API_PATHS.INTENSITY, data);

export const updateIntensitysite = (data: UpdateIntensity) =>
  httpClient.put(INTENSITYSITE_API_PATHS.INTENSITYSITE, data);

export const deleteSiteById = (id: number) =>
  httpClient.delete(SITES_API_PATHS.SITE(id));

export const uploadSites = ({
  file,
  overrideFlag = false,
}: {
  file: FormData;
  overrideFlag?: any;
}) => httpClient.post(SITES_API_PATHS.UPLOAD_SITE_MANAGERS, file, overrideFlag);

export const uploadintensity = ({ file }: { file: FormData }) =>
  httpClient.post(INTENSITYSITE_API_PATHS.UPLOAD_INTENSITY, file);

export const deleteCollectorById = (id: number) =>
  httpClient.delete(SITES_API_PATHS.DELETE_COLLECTOR(id));

export const deActivateEstimateById = (id: number) =>
  httpClient.put(SITES_API_PATHS.DEACTIVATE_ESTIMATES(id));

export const deleteEstimateById = (id: number) =>
  httpClient.delete(SITES_API_PATHS.DELETE_ESTIMATE(id));

export const deleteCompanyLevel = (id: number) =>
  httpClient.delete(INTENSITY_API_PATHS.DELETE_COMPANYLEVEL(id));

export const deleteSiteLevel = (id: number) =>
  httpClient.delete(INTENSITYSITE_API_PATHS.DELETE_SITELEVEL(id));

export const deleteResetLearning = () =>
  httpClient.delete(SITES_API_PATHS.RESET_LEARNING);

export const createEstimates = (body: CreateEstimateDTO) =>
  httpClient.post(SITES_API_PATHS.ESTIMATES, body);

export const updateEstimate = (data: UpdateEstimate) =>
  httpClient.put(SITES_API_PATHS.ESTIMATES, data);

export const getDeActivatedEstimatesBySiteId = (
  siteId: number,
): Promise<EstimateDTO[]> =>
  httpClient
    .get<EstimateDTO[]>(SITES_API_PATHS.DEACTIVATEDLIST_ESTIMATES(siteId))
    .then(getResData);

export const getRegion = (data: {
  country: string;
  zipCode: string | null | undefined;
}): Promise<string[]> =>
  httpClient.get(SITES_API_PATHS.GETREGION(data)).then(getResData);

export const exportSitesData = (): Promise<Blob> =>
  httpClient
    .get(SITES_API_PATHS.EXPORT_SITES_DATA, { responseType: "blob" })
    .then((response) => response.data);

export const getPurchases = (): Promise<any[]> =>
  httpClient
    .get<RenewableEnergyPurchasesDTO[]>(
      RENEWABLE_ENERGY_PURCHASES_API_PATHS.RENEWABLE_ENERGY_PURCHASES,
    )
    .then(getResData);

export const exportRenewableEnergyPurchasesData = (): Promise<Blob> =>
  httpClient
    .get(
      RENEWABLE_ENERGY_PURCHASES_API_PATHS.EXPORT_RENEWABLE_ENERGY_PURCHASES,
      {
        responseType: "blob",
      },
    )
    .then((response) => response.data);

export const createPurchase = (purchase: FormData) =>
  httpClient.post<RenewableEnergyPurchasesDTO>(
    RENEWABLE_ENERGY_PURCHASES_API_PATHS.RENEWABLE_ENERGY_PURCHASES,
    purchase,
  );

export const updatePurchase = (data: { id: number; purchase: FormData }) =>
  httpClient.put(
    RENEWABLE_ENERGY_PURCHASES_API_PATHS.GET_RENEWABLE_ENERGY_PURCHASE_BY_ID(
      data.id,
    ),
    data.purchase,
  );

export const deletePurchase = (id: number) =>
  httpClient.delete(
    RENEWABLE_ENERGY_PURCHASES_API_PATHS.DELETE_RENEWABLE_ENERGY_PURCHASE(id),
  );

export const updatePurchaseDistributions = (data: {
  id: number;
  distributions: DistributionDTO[];
}) =>
  httpClient.put(
    RENEWABLE_ENERGY_PURCHASES_API_PATHS.UPDATE_RENEWABLE_ENERGY_PURCHASE_DISTRIBUTIONS(
      data.id,
    ),
    data.distributions,
  );

export const deletePurchaseDocument = (id: number) =>
  httpClient.delete(
    RENEWABLE_ENERGY_PURCHASES_API_PATHS.DELETE_RENEWABLE_ENERGY_PURCHASE_DOCUMENT(
      id,
    ),
  );
